import {
  WA_SET_CONVERSATIONS,
  WA_APPEND_CONVERSATIONS,
  WA_MERGE_CONVERSATIONS,
  WA_SET_LOADING_MORE,
  WA_SET_MESSAGES,
  WA_SET_SELECTED,
  WA_APPEND_MESSAGE,
  WA_SET_LOADING,
  WA_SET_UNREAD_COUNT,
  WA_UPDATE_CONVERSATION,
  WA_SET_SESSIONS,
  WA_SET_SELECTED_SESSION,
  WA_SET_SESSION_QR,
  WA_SET_SESSION_MANAGER_OPEN,
  WA_UPDATE_SESSION,
  WA_WS_CONNECTED,
  WA_WS_DISCONNECTED,
  WA_UPDATE_MESSAGE_ACK,
  WA_REVOKE_MESSAGE,
  WA_UPDATE_MESSAGE,
  WA_SET_CHANNEL_UNREADS,
  WA_SET_ACTIVE_CHANNEL,
  WA_SET_TAB_COUNTS,
} from '../actions/whatsapp'

const initialState = {
  conversations: [],
  pagination: {},
  loadingMore: false,
  selectedConversation: null,
  messages: [],
  messagePagination: {},
  unreadCount: 0,
  loading: false,
  sessions: [],
  selectedSession: null,
  sessionQR: {},
  sessionManagerOpen: false,
  wsConnected: false,
  channelUnreads: { client: 0, partner: 0 },
  activeChannel: null,
  tabCounts: { pending: 0, in_progress: 0, resolved: 0 },
}

// Append page N (N > 1), dedupe by _id. Preserves existing order.
const appendConversations = (existing, incoming) => {
  const ids = new Set(existing.map(c => c._id))
  const fresh = incoming.filter(c => !ids.has(c._id))
  return [...existing, ...fresh]
}

// Merge (upsert) page-1 refresh into existing list without removing older pages.
// Updated docs overwrite matching _ids; new docs are appended at the top to
// preserve the "newest first" ordering the list is already sorted by.
const mergeConversations = (existing, incoming) => {
  if (!incoming.length) return existing
  const incomingById = new Map(incoming.map(c => [c._id, c]))
  const patched = existing.map(c =>
    incomingById.has(c._id) ? { ...c, ...incomingById.get(c._id) } : c
  )
  const existingIds = new Set(existing.map(c => c._id))
  const newOnes = incoming.filter(c => !existingIds.has(c._id))
  return [...newOnes, ...patched]
}

const whatsapp = (state = initialState, action) => {
  switch (action.type) {
    case WA_SET_CONVERSATIONS:
      return {
        ...state,
        conversations: action.payload.conversations,
        pagination: action.payload.pagination,
      }

    case WA_APPEND_CONVERSATIONS:
      return {
        ...state,
        conversations: appendConversations(
          state.conversations,
          action.payload.conversations
        ),
        pagination: action.payload.pagination,
      }

    case WA_MERGE_CONVERSATIONS: {
      const prevPages = state.pagination?.pages || 0
      const nextPagination = action.payload.pagination || {}
      return {
        ...state,
        conversations: mergeConversations(
          state.conversations,
          action.payload.conversations
        ),
        // Keep the larger "pages" if the user has already paginated so merges
        // don't falsely shrink the tracked page count.
        pagination: {
          ...nextPagination,
          pages: Math.max(prevPages, nextPagination.pages || 0),
          page: state.pagination?.page || nextPagination.page || 1,
        },
      }
    }

    case WA_SET_LOADING_MORE:
      return { ...state, loadingMore: action.payload }

    case WA_SET_MESSAGES:
      return {
        ...state,
        messages:
          action.payload.page > 1
            ? [...action.payload.messages, ...state.messages]
            : action.payload.messages,
        messagePagination: action.payload.pagination,
      }

    case WA_SET_SELECTED:
      return {
        ...state,
        selectedConversation: action.payload,
        messages: [],
        messagePagination: {},
      }

    case WA_APPEND_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
      }

    case WA_SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case WA_SET_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: action.payload,
      }

    case WA_UPDATE_CONVERSATION: {
      const updated = action.payload
      const exists = state.conversations.some(c => c._id === updated._id)
      // Don't insert conversations from other channels into the list
      const channelMismatch =
        !exists &&
        state.activeChannel &&
        updated.channel &&
        updated.channel !== state.activeChannel
      return {
        ...state,
        conversations: channelMismatch
          ? state.conversations
          : exists
          ? state.conversations.map(c =>
              c._id === updated._id ? { ...c, ...updated } : c
            )
          : [updated, ...state.conversations],
        selectedConversation:
          state.selectedConversation &&
          state.selectedConversation._id === updated._id
            ? { ...state.selectedConversation, ...updated }
            : state.selectedConversation,
      }
    }

    case WA_SET_SESSIONS:
      return {
        ...state,
        sessions: action.payload,
      }

    case WA_SET_SELECTED_SESSION:
      return {
        ...state,
        selectedSession: action.payload,
        // Clear conversation list when switching sessions
        conversations: [],
        pagination: {},
        selectedConversation: null,
        messages: [],
        messagePagination: {},
      }

    case WA_SET_SESSION_QR:
      return {
        ...state,
        sessionQR: {
          ...state.sessionQR,
          [action.payload.sessionId]: action.payload.qr,
        },
      }

    case WA_SET_SESSION_MANAGER_OPEN:
      return {
        ...state,
        sessionManagerOpen: action.payload,
      }

    case WA_UPDATE_SESSION:
      return {
        ...state,
        sessions: state.sessions.map(s =>
          s.sessionId === action.payload.sessionId
            ? { ...s, ...action.payload }
            : s
        ),
      }

    case WA_WS_CONNECTED:
      return { ...state, wsConnected: true }

    case WA_WS_DISCONNECTED:
      return { ...state, wsConnected: false }

    case WA_UPDATE_MESSAGE_ACK:
      return {
        ...state,
        messages: state.messages.map(m =>
          m.waMessageId === action.payload.waMessageId ||
          (action.payload.waMsgSerial &&
            m.waMsgSerial === action.payload.waMsgSerial)
            ? { ...m, ack: action.payload.ack }
            : m
        ),
      }

    case WA_REVOKE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(m =>
          m.waMessageId === action.payload.waMessageId ||
          (action.payload.waMsgSerial &&
            m.waMsgSerial === action.payload.waMsgSerial)
            ? { ...m, type: 'revoked', body: '' }
            : m
        ),
      }

    case WA_UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(m =>
          m._id === action.payload._id ? { ...m, ...action.payload } : m
        ),
      }

    case WA_SET_CHANNEL_UNREADS:
      return { ...state, channelUnreads: action.payload }

    case WA_SET_ACTIVE_CHANNEL:
      return { ...state, activeChannel: action.payload }

    case WA_SET_TAB_COUNTS:
      return {
        ...state,
        tabCounts: {
          pending: action.payload.pending || 0,
          in_progress: action.payload.in_progress || 0,
          resolved: action.payload.resolved || 0,
        },
      }

    default:
      return state
  }
}

export default whatsapp
